// src/pages/WartungPlanen.jsx
// Büro-Seite der Wartungsplanung: Station wählen, Termin setzen, Monteur
// zuweisen, Hinweise mitgeben.
//
// ZWEI Betriebsarten:
//   * Neu planen (ohne protokollId)
//   * Planung ändern (mit protokollId) - Anna-Lena am 11.09.2026: "wenn ich
//     eine Wartung einplane und einen Fehler mache, den ich erst nach dem
//     Speichern bemerke, kann ich die geplante Wartung nicht mehr anpassen".
//
// Beim Neu-Planen ist der Ablauf bewusst zweistufig:
//   1. Das Protokoll wird komplett angelegt (Datenbankfunktion aus Migration 21,
//      geht ganz oder gar nicht durch).
//   2. Danach werden Termin, Zuweisung und Hinweise eingetragen.
// Sollte Schritt 2 scheitern, existiert das Protokoll trotzdem und taucht in
// der Übersicht als Entwurf auf — nichts geht verloren, es fehlt nur die
// Zuweisung, die sich jetzt über "Planung ändern" nachtragen lässt.
//
// Station wechseln beim Ändern: Die Kapitel eines Protokolls werden für EINE
// Station angelegt (je Trafo ein Kapitel usw.). Ein Protokoll lässt sich also
// nicht auf eine andere Station umhängen. Stattdessen wird für die richtige
// Station ein neues angelegt und das falsche entfernt - aber nur, solange der
// Monteur darin noch nichts eingetragen hat. Messwerte gehen so nie verloren.

import { useState, useEffect, useRef } from 'react';
import { supabase, withTimeout, pruefpunkteDesProtokolls } from '../lib/supabase';
import { istBeantwortet } from '../lib/pruefpunktLogik';
import { createProtokollForStation } from '../hooks/useProtokoll';
import { fahrAnschrift } from '../hooks/useWartungen';
import Lieferscheine from './Lieferscheine';
import { frage } from './Frage';
import { useStationen } from '../hooks/useStationen';
import { anzeigeName } from '../lib/personen';
import { C, Field, TextInput, Select, Checkbox, Row } from './formui';

const OELPROBE = [
  { value: 'keine',   label: 'keine Ölprobe' },
  { value: 'normal',  label: 'Ölprobe normal' },
  { value: 'mit_pcb', label: 'Ölprobe mit PCB' },
];

// Solange ein Protokoll in einem dieser Zustände ist, hat der Monteur noch
// nicht angefangen - Station wechseln und Löschen sind dann erlaubt.
const NOCH_NICHT_BEGONNEN = ['vorbereitet', 'zugewiesen'];

const PLAN_SPALTEN = 'id, status, art, station_id, wartungs_datum, wartungs_uhrzeit, '
  + 'zugewiesen_an_id, zugewiesen_an, plan_oelprobe, plan_evu_dabei, '
  + 'plan_umz_schutzpruefung, plan_bemerkung, '
  + 'stationen(id, bezeichnung, standort_strasse, standort_ort, '
  + 'objekt_abweichend, objekt_name, objekt_strasse, objekt_plz, objekt_ort, kunden(name))';

// Hat der Monteur in diesem Protokoll schon etwas eingetragen?
// Ueber die Kapitel, nicht ueber eine protokoll_id - die hat die
// Pruefpunkt-Tabelle nicht (siehe pruefpunkteDesProtokolls).
//
// Nicht nur Pruefpunkte: Auch Erdungs-Messwerte und Fotos zaehlen, und ein
// Status "angefangen" auf dem Server. Vorher prueften wir nur die Antworten -
// wer mit den Ohm-Werten oder Fotos anfing, dem wurde die Wartung beim
// Stationswechsel samt Messwerten geloescht.
async function hatEintraege(protokollId) {
  const { data: p, error } = await withTimeout(
    supabase.from('protokolle').select('status').eq('id', protokollId).single(),
    20000, 'Status prüfen'
  );
  if (error) throw error;
  if (!NOCH_NICHT_BEGONNEN.includes(p?.status)) return true;
  const punkte = await pruefpunkteDesProtokolls(protokollId, 'wert_auswahl, wert_zahl, wert_bool, wert_text');
  if (punkte.some(istBeantwortet)) return true;
  const [m, f] = await Promise.all([
    supabase.from('protokoll_erderanlage_messungen').select('id', { count: 'exact', head: true }).eq('protokoll_id', protokollId),
    supabase.from('protokoll_fotos').select('id', { count: 'exact', head: true }).eq('protokoll_id', protokollId),
  ]);
  if (m.error) throw m.error;
  if (f.error) throw f.error;
  return (m.count || 0) > 0 || (f.count || 0) > 0;
}

// Geplante Wartung entfernen. Mängel zuerst: sie hängen mit ON DELETE SET NULL
// am Protokoll und blieben sonst herrenlos zurück. (Gleiches Vorgehen wie in
// der Protokoll-Übersicht.)
async function planungEntfernen(protokollId) {
  // Maengel-IDs merken: Nach dem Loeschen des Protokolls steht bei ihnen keine
  // protokoll_id mehr (ON DELETE SET NULL).
  const { data: mg, error: e0 } = await withTimeout(
    supabase.from('maengel').select('id').eq('protokoll_id', protokollId), 30000, 'Mängel suchen');
  if (e0) throw e0;
  // Nur loeschen, wenn der Monteur NICHT gerade angefangen hat (Status in der Datenbank).
  const { data: weg, error: e2 } = await withTimeout(
    supabase.from('protokolle').delete().eq('id', protokollId).in('status', NOCH_NICHT_BEGONNEN).select('id'),
    30000, 'Planung entfernen'
  );
  if (e2) throw e2;
  if (!weg || weg.length === 0) {
    throw new Error('Die Wartung wurde nicht gelöscht – sie wurde inzwischen begonnen oder gibt es nicht mehr.');
  }
  const ids = (mg || []).map(m => m.id);
  if (ids.length) {
    const { error: e1 } = await withTimeout(supabase.from('maengel').delete().in('id', ids), 30000, 'Mängel entfernen');
    if (e1) throw e1;
  }
}

// Offene Wartungen einer Station (geplant oder angefangen). Fuer die Warnung
// "fuer diese Station ist schon eine Wartung offen" - doppelte Planungen
// fielen bisher erst auf, wenn zwei Monteure vor derselben Tuer standen.
const OFFEN_STATUS = ['vorbereitet', 'zugewiesen', 'entwurf'];
const STATUS_KURZ = { vorbereitet: 'geplant', zugewiesen: 'zugewiesen', entwurf: 'angefangen' };
function datumDe(d) {
  const m = String(d || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? m[3] + '.' + m[2] + '.' + m[1] : 'ohne Datum';
}

// vorbelegung: aus dem Wartungsvertrag (planungAusVertrag) - Oelprobe, UMZ, Bemerkung.
export default function WartungPlanen({ protokollId = null, startStation = null, vorbelegung = null, onFertig, onAbbrechen }) {
  const bearbeiten = !!protokollId;

  const [suche, setSuche] = useState('');
  const s = useStationen({ search: suche });

  // startStation: aus der Liste "Fällige Wartungen" schon vorgewählt.
  const [station, setStation] = useState(startStation);
  // Wartung oder Sichtpruefung (Migration 47). Bei einer bestehenden Planung fest.
  const [art, setArt] = useState(vorbelegung?.art === 'sichtpruefung' ? 'sichtpruefung' : 'wartung');
  const istSicht = art === 'sichtpruefung';
  const [schonOffen, setSchonOffen] = useState([]);   // andere offene Wartungen dieser Station
  const [datum, setDatum] = useState('');
  const [uhrzeit, setUhrzeit] = useState('');
  const [monteurId, setMonteurId] = useState('');
  const [begleiter, setBegleiter] = useState('');
  const [oelprobe, setOelprobe] = useState(vorbelegung?.oelprobe || 'keine');
  const [evu, setEvu] = useState(false);
  const [umz, setUmz] = useState(!!vorbelegung?.umz);
  const [bemerkung, setBemerkung] = useState(vorbelegung?.bemerkung || '');

  // Beim Ändern: der gespeicherte Stand, um zu erkennen, was sich ändert.
  const [original, setOriginal] = useState(null);   // { status, station_id }
  const [geladen, setGeladen] = useState(!bearbeiten);
  const [ladeFehler, setLadeFehler] = useState(null);

  const [monteure, setMonteure] = useState([]);
  const [speichert, setSpeichert] = useState(false);
  // Schon angelegtes Protokoll aus einem frueheren Versuch dieses Formulars
  // (Anlegen klappte, danach scheiterte etwas): nicht noch einmal anlegen.
  const angelegt = useRef(null);
  const [fehler, setFehler] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await withTimeout(
          supabase.from('user_profiles').select('id, name, email, role, active')
            .eq('active', true).order('name'),
          20000, 'Mitarbeiter laden'
        );
        setMonteure(data || []);
      } catch { /* Ohne Liste bleibt "noch offen" - Planen geht trotzdem. */ }
    })();
  }, []);

  // Bestehende Planung laden
  useEffect(() => {
    if (!bearbeiten) return;
    let abgebrochen = false;
    (async () => {
      try {
        const { data, error } = await withTimeout(
          supabase.from('protokolle').select(PLAN_SPALTEN).eq('id', protokollId).single(),
          20000, 'Planung laden'
        );
        if (error) throw error;
        if (abgebrochen) return;
        setStation(data.stationen || null);
        setArt(data.art === 'sichtpruefung' ? 'sichtpruefung' : 'wartung');
        setDatum(data.wartungs_datum || '');
        setUhrzeit((data.wartungs_uhrzeit || '').slice(0, 5));
        setMonteurId(data.zugewiesen_an_id || '');
        setBegleiter(data.zugewiesen_an || '');
        setOelprobe(data.plan_oelprobe || 'keine');
        setEvu(!!data.plan_evu_dabei);
        setUmz(!!data.plan_umz_schutzpruefung);
        setBemerkung(data.plan_bemerkung || '');
        setOriginal({ status: data.status, station_id: data.station_id });
        setGeladen(true);
      } catch (e) {
        if (!abgebrochen) setLadeFehler(e.message || 'Die Planung konnte nicht geladen werden.');
      }
    })();
    return () => { abgebrochen = true; };
  }, [bearbeiten, protokollId]);

  // Gibt es fuer die gewaehlte Station schon eine offene Wartung?
  useEffect(() => {
    setSchonOffen([]);
    if (!station?.id) return undefined;
    let abgebrochen = false;
    (async () => {
      try {
        let q = supabase.from('protokolle').select('id, status, wartungs_datum, zugewiesen_an_id')
          .eq('station_id', station.id).in('status', OFFEN_STATUS).order('wartungs_datum');
        if (protokollId) q = q.neq('id', protokollId);
        const { data, error } = await withTimeout(q, 15000, 'Offene Wartungen prüfen');
        if (!error && !abgebrochen) setSchonOffen(data || []);
      } catch { /* ohne Netz keine Warnung - Planen geht trotzdem */ }
    })();
    return () => { abgebrochen = true; };
  }, [station?.id, protokollId]);

  const nochNichtBegonnen = !original || NOCH_NICHT_BEGONNEN.includes(original.status);
  const stationGewechselt = bearbeiten && station && original && station.id !== original.station_id;

  const planFelder = () => ({
    wartungs_datum: datum,
    wartungs_uhrzeit: uhrzeit || null,   // leer bleibt leer
    zugewiesen_an_id: monteurId || null,
    zugewiesen_an: begleiter.trim() || null,
    plan_oelprobe: istSicht || oelprobe === 'keine' ? null : oelprobe,
    plan_evu_dabei: evu,
    plan_umz_schutzpruefung: istSicht ? false : umz,
    plan_bemerkung: bemerkung.trim() || null,
  });

  // Erst die Planungsfelder, dann - getrennt und nur unter Bedingung - der
  // Status. Vorher wurde der Status beim Oeffnen des Formulars gemerkt und
  // beim Speichern blind zurueckgeschrieben: Hatte der Monteur inzwischen
  // abgeschlossen, setzte eine spaet gespeicherte Planungsaenderung sein
  // Protokoll wieder auf "zugewiesen".
  const planSpeichern = async (id, alterStatus) => {
    const { error: e } = await withTimeout(
      supabase.from('protokolle').update(planFelder()).eq('id', id),
      45000, 'Planung speichern'
    );
    if (e) throw e;
    if (alterStatus && !NOCH_NICHT_BEGONNEN.includes(alterStatus)) return;
    // Neu angelegt (Status "entwurf" aus der Datenbankfunktion) oder noch
    // nicht angefangen: Status nach Zuweisung. Nie aus einem spaeteren Stand.
    const erlaubt = alterStatus ? NOCH_NICHT_BEGONNEN : [...NOCH_NICHT_BEGONNEN, 'entwurf'];
    const { error: e2 } = await withTimeout(
      supabase.from('protokolle').update({ status: monteurId ? 'zugewiesen' : 'vorbereitet' })
        .eq('id', id).in('status', erlaubt),
      45000, 'Planung speichern (Status)'
    );
    if (e2) throw e2;
  };

  const speichern = async () => {
    if (speichert) return;
    if (!station) { setFehler('Bitte zuerst eine Station wählen.'); return; }
    if (!datum)   { setFehler('Bitte ein Wartungsdatum eintragen.'); return; }
    const neuFuerStation = !bearbeiten || stationGewechselt;
    if (neuFuerStation && schonOffen.length > 0 && !window.confirm(
      'Für diese Station ist schon ' + (schonOffen.length === 1 ? 'eine Wartung' : schonOffen.length + ' Wartungen') + ' offen:\n\n'
      + schonOffen.map(w => '  - ' + datumDe(w.wartungs_datum) + ' (' + (STATUS_KURZ[w.status] || w.status) + ')').join('\n')
      + '\n\nTrotzdem eine weitere Wartung anlegen?')) return;
    setSpeichert(true); setFehler(null);
    try {
      if (!bearbeiten) {
        let neueId = angelegt.current && angelegt.current.station === station.id ? angelegt.current.id : null;
        if (!neueId) {
          // Lief ein frueherer Versuch in die Zeitgrenze, hat die Datenbank das
          // Protokoll evtl. trotzdem angelegt. Dann dieses nehmen.
          const vor10Min = new Date(Date.now() - 10 * 60 * 1000).toISOString();
          let ich = null;
          try { ich = (await withTimeout(supabase.auth.getSession(), 5000, 'Anmeldung lesen'))?.data?.session?.user?.id || null; } catch { ich = null; }
          const { data: frisch, error: eFrisch } = ich ? await withTimeout(
            supabase.from('protokolle').select('id').eq('station_id', station.id).eq('erstellt_von_id', ich)
              .eq('status', 'entwurf').gte('erstellt_am', vor10Min).is('zugewiesen_an_id', null).limit(1),
            15000, 'Doppeltes Anlegen prüfen') : { data: null, error: null };
          if (eFrisch) throw eFrisch;
          if (frisch && frisch[0]) {
            // Frueher: "Abbrechen = eine weitere anlegen". Jetzt macht Abbrechen nichts.
            const wahl = await frage({
              titel: 'Schon angelegt?',
              text: 'Für diese Station wurde vor wenigen Minuten schon eine Wartung angelegt (vermutlich ein abgebrochener Versuch).',
              knoepfe: [
                { wert: 'verwenden', text: 'Diese Wartung verwenden', art: 'haupt' },
                { wert: 'neu', text: 'Eine weitere anlegen' },
              ],
            });
            if (wahl === 'verwenden') neueId = frisch[0].id;
            else if (wahl !== 'neu') return;
          }
        }
        if (!neueId) neueId = await createProtokollForStation(station.id, null, art);
        angelegt.current = { id: neueId, station: station.id };
        await planSpeichern(neueId, null);
        if (onFertig) onFertig(neueId);
        return;
      }

      if (stationGewechselt) {
        // Sicherheitsabfrage an der Datenbank, nicht nur am Status: Erst wenn
        // wirklich nichts eingetragen ist, darf das alte Protokoll weg.
        if (await hatEintraege(protokollId)) {
          throw new Error('In dieser Wartung wurde schon etwas eingetragen. Die Station lässt '
            + 'sich deshalb nicht mehr wechseln - sonst gingen Einträge verloren.');
        }
        // Beim zweiten Versuch (erster scheiterte nach dem Anlegen) das schon
        // angelegte Protokoll nehmen - sonst entstand jedes Mal ein weiteres.
        const neueId = angelegt.current?.station === station.id
          ? angelegt.current.id : await createProtokollForStation(station.id, null, art);
        angelegt.current = { id: neueId, station: station.id };
        await planSpeichern(neueId, null);
        await planungEntfernen(protokollId);
        if (onFertig) onFertig(neueId);
        return;
      }

      await planSpeichern(protokollId, original?.status);
      if (onFertig) onFertig(protokollId);
    } catch (e) {
      setFehler(e.message || 'Die Planung konnte nicht gespeichert werden.');
    } finally {
      setSpeichert(false);
    }
  };

  const loeschen = async () => {
    if (speichert) return;
    if (!window.confirm('Diese geplante Wartung wirklich löschen?\n\n'
      + 'Sie verschwindet auch beim Monteur von der Startseite und aus dem Kalender.')) return;
    setSpeichert(true); setFehler(null);
    try {
      if (await hatEintraege(protokollId)) {
        throw new Error('In dieser Wartung wurde schon etwas eingetragen. Sie lässt sich '
          + 'deshalb hier nicht löschen. Falls sie trotzdem weg soll: Protokolle → auswählen → Löschen.');
      }
      await planungEntfernen(protokollId);
      if (onFertig) onFertig(null);
    } catch (e) {
      setFehler(e.message || 'Die Planung konnte nicht gelöscht werden.');
    } finally {
      setSpeichert(false);
    }
  };

  const kopf = (
    <div style={{ background: C.navy, padding: '14px 16px', position: 'sticky', top: 0, zIndex: 40 }}>
      <div style={{ maxWidth: 760, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 12 }}>
        <button type="button" onClick={onAbbrechen}
          style={{ background: C.navyMid, color: C.gray300, border: '1px solid ' + C.navyLight,
                   borderRadius: 7, padding: '9px 14px', cursor: 'pointer', fontSize: 14, fontWeight: 700 }}>
          ‹ Zurück
        </button>
        <span style={{ color: C.white, fontWeight: 800, fontSize: 17 }}>
          {bearbeiten ? 'Planung ändern' : istSicht ? 'Sichtprüfung planen' : 'Wartung planen'}
        </span>
      </div>
    </div>
  );

  if (bearbeiten && !geladen) {
    return (
      <div style={{ background: C.bg, minHeight: '100vh' }}>
        {kopf}
        <div style={{ maxWidth: 760, margin: '0 auto', padding: '30px 14px', textAlign: 'center',
                      color: ladeFehler ? C.red : C.gray500, fontSize: 15, fontWeight: ladeFehler ? 700 : 400 }}>
          {ladeFehler || 'Planung wird geladen …'}
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: C.bg, minHeight: '100vh' }}>
      {kopf}

      <div style={{ maxWidth: 760, margin: '0 auto', padding: '18px 14px 40px' }}>

        {/* Art: Wartung oder Sichtpruefung */}
        <div style={{ background: C.white, borderRadius: 12, padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: C.gray500, textTransform: 'uppercase',
                        letterSpacing: 0.6, marginBottom: 10 }}>
            Was wird gemacht?
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {[['wartung', 'Wartung'], ['sichtpruefung', 'Sichtprüfung']].map(([wert, text]) => (
              <button key={wert} type="button" disabled={bearbeiten} onClick={() => setArt(wert)}
                style={{ flex: '1 1 160px', minHeight: 48, padding: '10px 14px', borderRadius: 9, fontWeight: 800, fontSize: 15,
                         border: '2px solid ' + (art === wert ? C.navy : C.gray200), background: art === wert ? C.navy : C.white,
                         color: art === wert ? C.white : C.gray700, cursor: bearbeiten ? 'default' : 'pointer', opacity: bearbeiten && art !== wert ? 0.5 : 1 }}>
                {text}
              </button>
            ))}
          </div>
          <div style={{ fontSize: 13, color: C.gray500, marginTop: 8 }}>
            {bearbeiten ? 'Die Art lässt sich bei einer bestehenden Planung nicht mehr ändern.'
              : istSicht ? 'Sichtprüfung ohne Abschaltung: ohne Schaltprüfungen und ohne Erdungsmessung, mit Thermografie.'
              : 'Vollständige Wartung mit allen Prüfpunkten.'}
          </div>
        </div>

        {/* Schritt 1: Station */}
        <div style={{ background: C.white, borderRadius: 12, padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: C.gray500, textTransform: 'uppercase',
                        letterSpacing: 0.6, marginBottom: 12 }}>
            1 · Welche Station?
          </div>

          {station ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            gap: 12, background: C.greenBg, border: '1.5px solid ' + C.green,
                            borderRadius: 9, padding: '13px 14px', flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontWeight: 800, color: C.navy, fontSize: 16 }}>
                    {station.kunden?.name || station.bezeichnung}
                  </div>
                  <div style={{ fontSize: 14, color: C.gray700 }}>
                    {[station.kunden?.name ? station.bezeichnung : null, fahrAnschrift(station)]
                      .filter(Boolean).join(' · ')}
                  </div>
                </div>
                {nochNichtBegonnen && (
                  <button type="button" onClick={() => setStation(null)}
                    style={{ minHeight: 44, padding: '9px 15px', border: '1.5px solid ' + C.navy,
                             borderRadius: 8, background: C.white, color: C.navy, fontWeight: 700,
                             fontSize: 14, cursor: 'pointer' }}>
                    Andere wählen
                  </button>
                )}
              </div>
              {schonOffen.length > 0 && (!bearbeiten || stationGewechselt) && (
                <div style={{ marginTop: 10, padding: '10px 12px', background: C.orangeBg || C.yellowBg,
                              borderLeft: '4px solid ' + (C.orange || C.yellow), borderRadius: 6,
                              fontSize: 14, color: C.navy, lineHeight: 1.45 }}>
                  <strong>Achtung:</strong> Für diese Station ist schon {schonOffen.length === 1 ? 'eine Wartung' : schonOffen.length + ' Wartungen'} offen
                  {' '}({schonOffen.map(w => datumDe(w.wartungs_datum) + ', ' + (STATUS_KURZ[w.status] || w.status)).join('; ')}).
                  Bitte prüfen, ob sie doppelt geplant wird.
                </div>
              )}
              {stationGewechselt && (
                <div style={{ marginTop: 10, padding: '10px 12px', background: C.yellowBg,
                              borderLeft: '4px solid ' + C.yellow, borderRadius: 6,
                              fontSize: 14, color: C.navy, lineHeight: 1.45 }}>
                  Du hast eine andere Station gewählt. Beim Speichern wird die Wartung für diese
                  Station neu angelegt und die bisherige entfernt.
                </div>
              )}
              {bearbeiten && !nochNichtBegonnen && (
                <div style={{ marginTop: 10, fontSize: 14, color: C.gray500, lineHeight: 1.45 }}>
                  Der Monteur hat diese Wartung schon begonnen — die Station lässt sich deshalb
                  nicht mehr wechseln. Termin, Monteur und Hinweise kannst du weiter ändern.
                </div>
              )}
            </>
          ) : (
            <>
              <TextInput value={suche} onChange={setSuche}
                placeholder="Kunde, Station, Ort oder Straße …"/>
              <div style={{ fontSize: 13, color: C.gray500, marginTop: 5 }}>
                {suche
                  ? (s.count > 0 ? s.count + (s.count === 1 ? ' Station gefunden' : ' Stationen gefunden') : '')
                  : 'Tippe los — oder wähle unten aus der Liste.'}
              </div>
              <div style={{ maxHeight: 320, overflowY: 'auto', marginTop: 10 }}>
                {s.loading && (s.stationen || []).length === 0 &&
                  <div style={{ padding: 14, color: C.gray500, fontSize: 15 }}>lädt …</div>}
                {!s.loading && (s.stationen || []).length === 0 && (
                  <div style={{ padding: '14px 2px', color: C.gray500, fontSize: 15, lineHeight: 1.5 }}>
                    Nichts gefunden {suche && <>zu „<strong>{suche}</strong>"</>}.<br />
                    Du kannst nach dem <strong>Kundennamen</strong>, der Stationsbezeichnung,
                    dem Ort, der Straße oder der Bezeichnung beim VNB suchen.
                  </div>
                )}
                {(s.stationen || []).slice(0, 40).map(st => (
                  <button key={st.id} type="button" onClick={() => setStation(st)}
                    style={{ display: 'block', width: '100%', textAlign: 'left', background: C.white,
                             border: '1.5px solid ' + C.gray200, borderRadius: 9, padding: '12px 13px',
                             marginBottom: 7, cursor: 'pointer', fontFamily: 'inherit' }}>
                    <div style={{ fontWeight: 700, color: C.navy, fontSize: 15 }}>
                      {st.kunden?.name || st.bezeichnung}
                    </div>
                    <div style={{ fontSize: 14, color: C.gray700 }}>
                      {[st.kunden?.name ? st.bezeichnung : null, fahrAnschrift(st)]
                        .filter(Boolean).join(' · ')}
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Schritt 2: Termin */}
        <div style={{ background: C.white, borderRadius: 12, padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: C.gray500, textTransform: 'uppercase',
                        letterSpacing: 0.6, marginBottom: 12 }}>
            2 · Wann?
          </div>
          <Row columns={2}>
            <Field label="Datum" required>
              <input type="date" value={datum} onChange={e => setDatum(e.target.value)}
                style={{ width: '100%', padding: '12px', borderRadius: 7, fontSize: 16, minHeight: 48,
                         border: '1.5px solid ' + C.gray200, boxSizing: 'border-box',
                         background: C.white, fontFamily: 'inherit' }}/>
            </Field>
            <Field label="Uhrzeit" hint="Optional — leer lassen, wenn noch offen">
              <input type="time" value={uhrzeit} onChange={e => setUhrzeit(e.target.value)}
                style={{ width: '100%', padding: '12px', borderRadius: 7, fontSize: 16, minHeight: 48,
                         border: '1.5px solid ' + C.gray200, boxSizing: 'border-box',
                         background: C.white, fontFamily: 'inherit' }}/>
            </Field>
          </Row>
          {uhrzeit && (
            <button type="button" onClick={() => setUhrzeit('')}
              style={{ minHeight: 40, padding: '7px 13px', borderRadius: 7, border: '1.5px solid ' + C.gray200,
                       background: C.white, color: C.gray700, fontWeight: 600, fontSize: 14,
                       cursor: 'pointer', fontFamily: 'inherit' }}>
              Uhrzeit wieder offen lassen
            </button>
          )}
        </div>

        {/* Schritt 3: Wer */}
        <div style={{ background: C.white, borderRadius: 12, padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: C.gray500, textTransform: 'uppercase',
                        letterSpacing: 0.6, marginBottom: 12 }}>
            3 · Wer fährt?
          </div>
          <Field label="Zuständiger Monteur" hint="Er sieht die Wartung auf seiner Startseite. Später änderbar, falls jemand ausfällt.">
            <Select value={monteurId} onChange={v => setMonteurId(v || '')}
              placeholder="-- noch offen --"
              options={monteure.map(m => ({ value: m.id, label: anzeigeName(m) }))}/>
          </Field>
          <Field label="Begleiter" hint="Freitext, z. B. „Dylka, Urban“">
            <TextInput value={begleiter} onChange={setBegleiter} placeholder="optional"/>
          </Field>
        </div>

        {/* Schritt 4: Hinweise */}
        <div style={{ background: C.white, borderRadius: 12, padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: C.gray500, textTransform: 'uppercase',
                        letterSpacing: 0.6, marginBottom: 4 }}>
            4 · Hinweis für den Monteur
          </div>
          <div style={{ fontSize: 14, color: C.gray700, marginBottom: 12, lineHeight: 1.45 }}>
            Steht auf seiner Startseite, bevor er losfährt — er weiß dann, was er einpacken muss.
          </div>
          {!istSicht && (
            <Field label="Ölprobe">
              <Select value={oelprobe} onChange={v => setOelprobe(v || 'keine')} options={OELPROBE}
                placeholder={null}/>
            </Field>
          )}
          <Checkbox checked={evu} onChange={setEvu} label="EVU kommt zum Termin dazu"/>
          {!istSicht && <Checkbox checked={umz} onChange={setUmz} label="UMZ-Schutzprüfung durchführen"/>}
          <Field label="Anmerkung">
            <TextInput value={bemerkung} onChange={setBemerkung}
              placeholder="z. B. Schlüssel beim Pförtner abholen"/>
          </Field>
        </div>

        {/* Lieferscheine gibt es erst, wenn die Wartung angelegt ist (sie haengen am Protokoll). */}
        <div style={{ background: C.white, borderRadius: 12, padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: C.gray500, textTransform: 'uppercase',
                        letterSpacing: 0.6, marginBottom: 8 }}>
            5 · Lieferscheine
          </div>
          {bearbeiten ? (
            <Lieferscheine protokollId={protokollId} stationId={station?.id} darfHochladen darfUnterschreiben={false}/>
          ) : (
            <div style={{ fontSize: 14, color: C.gray700, lineHeight: 1.45 }}>
              Nach dem Einplanen über „Planung ändern“ hochladen – der Monteur lässt sie dann vor Ort vom Kunden unterschreiben.
            </div>
          )}
        </div>

        {fehler && (
          <div style={{ background: C.redBg, border: '1.5px solid ' + C.red, borderRadius: 9,
                        padding: '13px 15px', marginBottom: 14, fontSize: 15, color: C.navy, lineHeight: 1.5 }}>
            {fehler}
          </div>
        )}

        <button type="button" onClick={speichern} disabled={speichert}
          style={{ width: '100%', minHeight: 56, background: speichert ? C.gray300 : C.green,
                   color: C.white, border: 'none', borderRadius: 10, fontSize: 17, fontWeight: 800,
                   cursor: speichert ? 'default' : 'pointer' }}>
          {speichert
            ? (bearbeiten ? 'wird gespeichert …' : 'wird angelegt …')
            : (bearbeiten ? 'Änderungen speichern' : 'Wartung einplanen')}
        </button>

        {bearbeiten && nochNichtBegonnen && (
          <button type="button" onClick={loeschen} disabled={speichert}
            style={{ width: '100%', minHeight: 48, marginTop: 12, background: C.white,
                     color: C.red, border: '1.5px solid ' + C.red, borderRadius: 10,
                     fontSize: 15, fontWeight: 700, cursor: speichert ? 'default' : 'pointer' }}>
            Geplante Wartung löschen
          </button>
        )}
      </div>
    </div>
  );
}
